import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { AlertService } from '../../_alert';
import { BasketTimerService } from '../../services/basket-timer.service';
import { ApiService } from '../../services/jrni/api.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { BasketService } from '../../services/jrni/basket.service';
import { DepartmentService } from '../../services/jrni/department.service';
import { ServicesService } from '../../services/jrni/services.service';

/** Screen readers hear the time left at these minute marks. */
const ANNOUNCE_AT = [10, 5, 1];

/**
 * Basket countdown box, copied from the Shropshire desk and room app.
 * It shows how long the selected slot is held. The customer can extend it once.
 */
@Component({
	selector: 'app-basket-timer',
	templateUrl: './basket-timer.component.html',
	styleUrls: ['./basket-timer.component.scss'],
})
export class BasketTimerComponent implements OnInit, OnDestroy {
	remaining = '';
	extended = false;
	announcement = '';
	private lastMinutes: number;
	private tick: Subscription;

	constructor(
		private basketTimer: BasketTimerService,
		private router: Router,
		private translate: TranslateService,
		private spinner: NgxSpinnerService,
		private alertService: AlertService,
		private apiService: ApiService,
		private availabilityService: AvailabilityService,
		private basketService: BasketService,
		private departmentService: DepartmentService,
		private servicesService: ServicesService
	) {}

	ngOnInit(): void {
		this.update();
		this.tick = interval(1000).subscribe(() => this.update());
	}

	ngOnDestroy(): void {
		this.tick?.unsubscribe();
	}

	private update(): void {
		const state = this.basketTimer.state;
		if (!state) return;
		this.extended = state.extended;

		const seconds = Math.max(0, Math.floor((state.end - Date.now()) / 1000));
		this.remaining = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

		const minutes = Math.ceil(seconds / 60);
		if (this.lastMinutes !== undefined && minutes !== this.lastMinutes && ANNOUNCE_AT.includes(minutes)) {
			this.announcement = this.translate.instant(
				minutes === 1 ? 'BASKET_TIMER.MINUTE_LEFT' : 'BASKET_TIMER.MINUTES_LEFT',
				{ minutes }
			);
		}
		this.lastMinutes = minutes;

		if (seconds === 0) {
			this.leave('BASKET_TIMER.EXPIRED');
		}
	}

	/**
	 * Same as Shropshire: make the basket again so that JRNI holds the slot again from now.
	 * The slot is free for a moment, so someone else can take it. Then the customer must pick again.
	 */
	async extend(): Promise<void> {
		this.spinner.show();
		try {
			const location = await this.departmentService.getLocation();
			const slot = await this.availabilityService.getSelectedSlot();
			const resource = await this.availabilityService.getSelectedResource();
			const service = await this.servicesService.get();

			await this.apiService.deleteBaskets(location);
			const response = await firstValueFrom(this.apiService.createBasket(location));
			if (response.headers.has('auth-token')) {
				localStorage.setItem('auth_token', response.headers.get('auth-token'));
			}
			// addServiceItem returns the error message as a string when the call fails.
			const serviceItem: any = await this.apiService.addServiceItem(
				location,
				response.body,
				slot.start,
				service,
				[],
				service.calculated_duration,
				resource
			);
			if (!serviceItem?.id) {
				throw new Error(serviceItem);
			}
			this.basketService.currentBasket = response.body;
			this.basketService.currentServiceItem = serviceItem;
			this.basketTimer.start(true);
			this.update();
		} catch (err) {
			console.error(err);
			this.leave('SUMMARY.INVALID_SLOT');
		} finally {
			this.spinner.hide();
		}
	}

	/** The calendar deletes the old basket when it loads, so the slot is released there. */
	private leave(message: string): void {
		this.basketTimer.stop();
		this.alertService.error(this.translate.instant(message), { keepAfterRouteChange: true });
		this.router.navigate(['/calendar']);
	}
}
